import React from 'react';
import { observer } from 'mobx-react';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import Button from 'components/button/button';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { Theme } from '@seedlang/constants';
import { computed } from 'mobx';
import { Link } from 'react-router';
import autobind from 'autobind-decorator';

const Wrapper = styled.div`
  padding: 20px;
  background: white;
  border-radius: ${props => props.borderRadius};
`;

const AdminLink = styled.div`
  margin-top: 5px;
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  padding: 5px;
`;

const Table = styled.table`
  width: 100%;
  td {
    padding: 5px;
    vertical-align: middle;
  }
  tr.clickable {
    cursor: pointer;
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
`;

const RSSLink = styled.span`
  display: flex;
  font-size: 14px;
  align-items: center;
  margin: 0 10px;
  color: #333333;
  cursor: pointer;
  span {
    text-decoration: underline;
  }
  i {
    margin-right: 5px;
  }
  span:hover {
    text-decoration: none;
  }
  justify-content: ${props => props.justifyContent};
`;

@observer
class DownloadLinkIndex extends React.Component {

  @computed get worksheet() {
    if (isPresent(this.props.worksheet)) {
      return this.props.worksheet;
    } else if (ExerciseUI.hasWorksheet) {
      return ExerciseUI.worksheet;
    }
  }

  @autobind blockedUntilMigrated(downloadLink) {
    return downloadLink.blockIfNotMigrated && AppUI.shouldBlockPerksForMigration;
  }

  getButtonConfig(downloadLink) {
    const isBlocked = !AppUI.siteIsDefault && downloadLink.blocked;
    const needsToCreateAccount = isBlocked && ExerciseUI.worksheet.accessWithoutMembership;
    const needsToMigrate = !AppUI.siteIsDefault && this.blockedUntilMigrated(downloadLink) && !downloadLink.blocked;
    const needsToBecomeMember = !ExerciseUI.user?.isMember && isBlocked && !ExerciseUI.worksheet.accessWithoutMembership;
    const needsToUpgradeMembershipType = ExerciseUI.user?.isMember && isBlocked && !ExerciseUI.worksheet.accessWithoutMembership;

    if (needsToCreateAccount) {
      return { locked: true, to: { name: 'onboarding.register' }, text: 'Create Free Account to Try' };
    }
    if (needsToBecomeMember) {
      return { locked: true, to: { name: 'memberships.index' }, text: 'Become a Member to Unlock' };
    }
    if (needsToUpgradeMembershipType) {
      return { locked: true, to: { name: 'memberships.index' }, text: 'Upgrade to Unlock' };
    }
    if (needsToMigrate) {
      return { locked: true, to: { name: 'settings.billing' }, text: 'Migrate Payment' };
    }

    if (isBlocked) return null;

    return {
      to: { pathname: downloadLink.text },
      text: 'Download',
    };
  }

  render() {
    return (
      <Wrapper borderRadius={this.props.borderRadius}>
        {
          isPresent(this.worksheet) &&
            <Table className={`${AppUI.layout.isDesktop ? 'table-striped' : ''} table-hover table-responsive`}>
              <tbody>
                {
                  this.worksheet.downloadLinks.map(item => {
                    const buttonConfig = this.getButtonConfig(item);
                    return (
                      <React.Fragment key={item.id}>
                        <tr
                          className={buttonConfig.to.pathname ? 'clickable' : ''}
                        >
                          <td width='20px'>
                            <i className={`fa fa-${item.icon || 'link'}`} style={{margin: '5px 10px 5px 20px'}}/>
                          </td>
                          <td
                            style={{display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'space-between'}}
                          >
                            <span
                              onClick={buttonConfig.to.pathname ? () => {
                                const link = document.createElement('a');
                                link.href = item.text;
                                link.target = '_blank';
                                link.rel = 'noopener noreferrer';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              } : undefined}
                              style={{flex: 1}}
                            >
                              {item.title}
                              {isPresent(item.subtitle) && ` - ${item.subtitle}`}
                            </span>
                            {
                              !item.blocked && isPresent(item.podcastId) && !AppUI.layout.isMobile &&
                                <Link
                                  to={{name: 'settings.audio_feeds', params: {podcastId: item.podcastId}}}
                                  target='_blank'
                                >
                                  <RSSLink>
                                    <i className='fa fa-rss' /><span>Also available as a podcast feed</span>
                                  </RSSLink>
                                </Link>
                            }
                          </td>
                          <td width={AppUI.layout.isMobile ? '50px' : '280px'}>
                            {
                              buttonConfig && (
                                <Link
                                  to={buttonConfig.to}
                                  {...(buttonConfig.to.pathname ? { target: '_blank' } : {})}
                                >
                                  <Button
                                    height='30px'
                                    width='98%'
                                    fontSize='14px'
                                    color='#FFF'
                                    borderRadius='5px'
                                    scaleOnHover={AppUI.layout.isMobile ? null : '1.03'}
                                    backgroundOnActive={Theme.gray}
                                    iconMarginRight={AppUI.layout.isMobile ? '0px' : '3px'}
                                    background={buttonConfig.locked ? '#AAA' : (AppUI.site.accentColor || '#716f6f')}
                                  >
                                    <i className={`fa fa-${buttonConfig.locked ? 'lock' : 'download'}`}/>
                                    {AppUI.layout.isMobile ? '' : buttonConfig.text}
                                  </Button>
                                </Link>
                              )
                            }
                          </td>
                        </tr>
                        {
                          !item.blocked && isPresent(item.podcastId) && AppUI.layout.isMobile &&
                            <tr>
                              <td colSpan='3'>
                                <Link
                                  to={{name: 'settings.audio_feeds', params: {podcastId: item.podcastId}}}
                                  target='_blank'
                                >
                                  <RSSLink justifyContent='flex-end'>
                                    <i className='fa fa-rss' /><span>Also available as a podcast feed</span>
                                  </RSSLink>
                                </Link>
                              </td>
                            </tr>
                        }
                      </React.Fragment>
                    );
                  })
                }
              </tbody>
            </Table>
        }
        {
          !AppUI.siteIsDefault && ExerciseUI.user.anyAdmin && this.worksheet && this.worksheet.groupId &&
          <AdminLink>
            <Link
              to={{name: 'creator.worksheets.edit', params: {groupId: this.worksheet.groupId, worksheetId: this.worksheet.id}}}
            >
              Admin Link
            </Link>
          </AdminLink>
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(DownloadLinkIndex);
